/* DeviceGridViewRenderer.js */

"use strict";
/* global kendo, $ */
const EntityGrid = require("@gluenetworks/entity_grid_gl").Grid;
const Registrations = require("../../../../../RegistrationNamespaces");
const DistinctValuesFeed = require("../../../../lib/DistinctValuesFeed");
const TreeUtils = require("../../../siteexplorer/treeUtilities.js");
const _ = require("lodash");

class DeviceGridViewRenderer {
    constructor(model) {
        this._model = model;
        this._rootElem = document.createElement("device-grid-view-gl");
        this._logger = this._model.logger;
        this._treeContainer = document.createElement("tree-container");
        this._gridContainer = document.createElement("grid-container-wrapper");
        this._resizer = document.createElement("div");
        this._resizer.className = "resizer";
        this._resizer.id = "resizer";
        this._documentWidth = document.body.clientWidth;
        this._resizer.style.width = (this._documentWidth * 0.3) / 100 + "px";
        this._treeContainer.style.width = 200 + "px";
        this._panelEl = document.createElement("span");
        this._panelEl.classList.add("active", "treeview-toggle");
        this._panelEl.setAttribute("title", "Sites");
        this._panelEl.innerHTML = `<actionable-icon-gl data-icon="topology.solution" data-dark-theme="true" class="dark"></actionable-icon-gl>`;

        this._expandTree = _.debounce(this._expandTree.bind(this), 100);
        this._setExpandedSitesData = _.debounce(this._setExpandedSitesData.bind(this), 200);
        this._updateFilter = this._updateFilter.bind(this);
        this._moCallback = this._moCallback.bind(this);
        this._userDataStorage = this._model._storageFactory.getRemoteStorageInstance(
            "view",
            "device_details"
        );
        this._defaultViewActive = false;
        // Deferred tree regeneration: skip expensive rebuilds when tree is hidden
        this._isTreeVisible = true;
        this._hasPendingTreeRegeneration = false;
    }

    /********************************************/
    /* START PRIVATE COMPONENT RENDERER METHODS */
    /********************************************/

    _registerListeners() {
        this._model.on(
            "FeedInitialized",
            (this._onFeedInitialized = this._onFeedInitialized.bind(this))
        );

        // this._model.gameEditor.on('ColumnSettingsChange', this._onColumnSettingsChanged = this._onColumnSettingsChanged.bind(this));
        this._model.on(
            "GridColumnUpdated",
            (this._onGridColumnUpdated = this._onGridColumnUpdated.bind(this))
        );
        this._model.pagingFeed.on(
            "RefreshView",
            (this._onFeedRefreshView = this._onFeedRefreshView.bind(this))
        );

        this._model._gameEditor._LMRBLayout.on(
            "SelectRowsOnDeviceGrid",
            (this._onSelectRowsOnDeviceGrid = this._onSelectRowsOnDeviceGrid.bind(this))
        );
        this._model._gameEditor.on(
            "OrganizationChanged",
            (this._onOrgChanged = this._onOrgChanged.bind(this))
        );

        this._panelEl.removeEventListener("click", this._toggleTree);
        this._panelEl.addEventListener("click", (this._toggleTree = this._toggleTree.bind(this)));

        this._resizer.addEventListener(
            "mousedown",
            (this._onResizerMousedown = this._onResizerMousedown.bind(this))
        );
        window.addEventListener(
            "resize",
            (this._onWindowResize = () => {
                this._documentWidth = document.body.clientWidth;
                this._treeContainer.style.width =
                    (this._userDataStorage.getData("treeContainerWidth", true) || 200) + "px";
            })
        );
        this._model._gameEditor.on(
            "RegenerateTreeData",
            (this._onRegenerateTreeData = this._onRegenerateTreeData.bind(this))
        );
        this._registerGridListeners();
    }

    _registerGridListeners() {
        this._deviceEntityGrid.on(
            "UserSelectedRows",
            (this._onGridUserSelectedRows = this._onGridUserSelectedRows.bind(this))
        );
        this._deviceEntityGrid.on(
            "UserRowDoubleClicked",
            (this._onUserDblClickedRow = this._onUserDblClickedRow.bind(this))
        );
        this._deviceEntityGrid.on(
            "UserSorted",
            (this._onUserSorted = this._onUserSorted.bind(this))
        );
        this._deviceEntityGrid.on(
            "GridBusyStart",
            (this._onGridBusyStart = this._onGridBusyStart.bind(this))
        );
        // this._deviceEntityGrid.on("BasicSearchFilterChangeData", (this._onFilterChange = this._onFilterChange.bind(this)));
        // this._deviceEntityGrid.on("AdvancedSearchFilterChangeData", (this._onFilterChange = this._onFilterChange.bind(this)));
    }

    _unregisterListeners() {
        if (this._model) {
            this._model.removeListener("FeedInitialized", this._onFeedInitialized);
            this._model.removeListener("GridColumnUpdated", this._onGridColumnUpdated);
        }
        if (this._resizer) {
            this._resizer.removeEventListener("mousedown", this._onResizerMousedown);
        }
        window.removeEventListener("resize", this._onWindowResize);
        if (this._panelEl) {
            this._panelEl.removeEventListener("click", this._toggleTree);
        }
        if (this._model.pagingFeed) {
            this._model.pagingFeed.removeListener("RefreshView", this._onFeedRefreshView);
        }
        if (this._model._gameEditor._LMRBLayout) {
            this._model._gameEditor._LMRBLayout.removeListener(
                "SelectRowsOnDeviceGrid",
                this._onSelectRowsOnDeviceGrid
            );
        }
        if (this._model._gameEditor) {
            this._model._gameEditor.removeListener(
                "RegenerateTreeData",
                this._onRegenerateTreeData
            );
            this._model._gameEditor.removeListener("OrganizationChanged", this._onOrgChanged);
        }
        if (this._deviceEntityGrid) {
            this._unregisterGridListeners();
        }
    }

    _unregisterGridListeners() {
        this._deviceEntityGrid.removeAllListeners("UserSelectedRows");
        this._deviceEntityGrid.removeAllListeners("UserRowDoubleClicked");
        this._deviceEntityGrid.removeListener("UserSorted", this._onUserSorted);
        this._deviceEntityGrid.removeListener("GridBusyStart", this._onGridBusyStart);
        // this._deviceEntityGrid.removeListener("BasicSearchFilterChangeData", this._onFilterChange);
        // this._deviceEntityGrid.removeListener("AdvancedSearchFilterChangeData", this._onFilterChange);
    }

    async _renderAvailableActions() {
        let numSelected = this.selectedRows.length || 0;
        let rowCount = this.model.pagingFeed.totalEntities || 0;
        // this._model.gameEditor.hideViewAction(Registrations.DEVICE_EXPLORER_VIEW_GRID);
        if (numSelected > 0) {
            this._model.gameEditor.enableAllActions();
        } else {
            this._model.gameEditor.disableAllActions();
            this._model.gameEditor.enableAction(
                Registrations.DEVICE_EXPLORER_ACTION_CREATE_OS_PLAN
            );
            this._model.gameEditor.enableAction(Registrations.DEVICE_EXPLORER_ACTION_CREATE);
            this._model.gameEditor.enableAction(Registrations.DEVICE_EXPLORER_ACTION_IMPORT);
            this._model.gameEditor.enableAction(Registrations.DEVICE_EXPLORER_ACTION_EXPORT);
            this._model.gameEditor.enableAction(Registrations.DEVICE_EXPLORER_ACTION_ADHOC_QUERY);
        }

        if (numSelected > 1) {
            this._model.gameEditor.disableAction(Registrations.DEVICE_EXPLORER_ACTION_CLONE);
        }

        if (rowCount > 0) {
            this._model.gameEditor.enableAction(Registrations.DEVICE_EXPLORER_ACTION_REPORT);
        } else {
            this._model.gameEditor.disableAction(Registrations.DEVICE_EXPLORER_ACTION_REPORT);
        }

        if (
            this._model._solutionName === "device_manager" &&
            (this.model.isCiscoSupportEnabled === true ||
                this.model
                    .isNistSupportEnabled) /* && !this._model.gameEditor._cabinetCmpnt._registeredActions.find((actionItem) => actionItem.fullyQualifiedName === Registrations.DEVICE_EXPLORER_ACTION_UPDATE_CISCO)*/
        ) {
            /*this.model.gameEditor.addBarActions([
                {fullyQualifiedName: Registrations.DEVICE_EXPLORER_ACTION_UPDATE_CISCO, shown: true, icon: "devicemanager.api_update", title: "Update Support Data", order: 2}
            ]);*/
        } else {
            this.model.gameEditor.hideBarAction(Registrations.DEVICE_EXPLORER_ACTION_UPDATE_CISCO);
        }

        if (numSelected === 1) {
            let selectedDevice = this._model.pagingFeed.getDeviceEntityById(this.selectedRows[0]);
            if (!selectedDevice) {
                selectedDevice = await this.model._DeviceEntityDef.loadDevice(
                    this.model.activeOrgId,
                    this.selectedRows[0],
                    {controlType: true}
                );
            }
            // Note that hybrid controlled devices can be cloned
            if (selectedDevice && selectedDevice.getBaseControlType() === "CONTROLLED") {
                this._model.gameEditor.disableAction(Registrations.DEVICE_EXPLORER_ACTION_CLONE);
            }
        }
    }

    /*******************************************/
    /* END PRIVATE COMPONENT RENDERER METHODS  */
    /* START PUBLIC COMPONENT RENDERER METHODS */
    /*******************************************/

    shutdown() {
        this._unregisterListeners();
        if (this._moObserver) {
            this._moObserver.disconnect();
        }
        let promArr = [
            _.isUndefined(this._deviceEntityGrid)
                ? Promise.resolve()
                : this._deviceEntityGrid.shutdown(),
        ];

        return Promise.all(promArr).then(
            () => {
                this._deviceEntityGrid = undefined;
                this._defaultViewActive = undefined;
                this._model = undefined;
                this._rootElem.remove();
                this._rootElem = undefined;
            },
            (err) => {
                this.model._logger.warn(err, "DeviceGridViewRenderer.shutdown() error");
            }
        );
    }

    /**
     * Stateless method to completely rebuild this component from the _rootElem down.
     * @returns {Promise} An ES6 promise that lets the caller know when the component has finished rendering. If a non
     * Promise is returned the caller will assume the Renderer has completed rendering.
     */
    render() {
        // const treeFilterData = this._userDataStorage.getData("DE_site_filter_data");
        const selectedSite = this._userDataStorage.getData("site_tree_selected");
        return this._model
            .whenModelIsReady(selectedSite)
            .then(() => {
                const additionalFilter = [
                    {
                        dataType: "array",
                        label: "CVEs",
                        name: "cves",
                        options: "",
                    },
                    {
                        dataType: "longstring",
                        label: "Latest Configuration Snapshot",
                        name: "latestConfig",
                        options: [],
                    },
                    {
                        dataType: "longstring",
                        label: "Latest Operational Snapshot",
                        name: "stateData",
                        options: [],
                    },
                ];

                if (this._model._CDALicenseEnabled) {
                    additionalFilter.push({
                        dataType: "longstring",
                        label: "Default Configuration Snapshot",
                        name: "defaultConfig",
                        options: [],
                    });
                }

                const optionMapSitePaths = this._model.gameEditor.sitePaths.map(({id, path}) => ({
                    [id]: path,
                }));
                // optionMapSitePaths.unshift({null: "/"});
                additionalFilter.push({
                    name: "siteId",
                    label: "Site Path List",
                    dataType: "pathlist",
                    options: optionMapSitePaths,
                });

                // Filter-only entry (no grid column). Always offered, even with no tags in
                // the org, so saved Tags rules stay visible instead of silently applying.
                additionalFilter.push({
                    name: "tagIds",
                    label: "Tags",
                    dataType: "array",
                    options: this._model._tagOptions || [],
                    forceSelect: true,
                });

                additionalFilter.push({
                    dataType: "array",
                    label: "Exposure Affected CVEs",
                    name: "exposureExposedCves",
                    options: "",
                });

                // if(this._model._solutionName === "device_manager") {
                if (
                    this._model.gameEditor.sitePaths &&
                    this._model.gameEditor.sitePaths.length === 0
                ) {
                    this._treeContainer.classList.add("hidden");
                    this._resizer.classList.add("hidden");
                }
                const toggleData = this._userDataStorage.getData("site_tree_toggle_data", true);
                // console.log(toggleData, this.model.activeOrgId);
                if (toggleData) {
                    const toggleState = toggleData[this.model.activeOrgId];
                    if (!toggleState) {
                        this._treeContainer.classList.add("hidden");
                        this._resizer.classList.add("hidden");
                    } else {
                        this._treeContainer.classList.remove("hidden");
                        this._resizer.classList.remove("hidden");
                    }
                }
                this._renderSiteTree(selectedSite);

                this._treeContainer.style.width =
                    (this._userDataStorage.getData("treeContainerWidth", true) || 200) + "px";
                // }

                // if (selectedSite) {
                //     // console.log(selectedSite, this.treeData);
                //     const pathVals = TreeUtils.getAllChildrenPathsFromId(this._model.treeData, selectedSite);
                //     if (pathVals) {
                //         this._filterForTree = { sitePath: { $in: pathVals } };
                //     }
                //     // this._updateFilter("ontoggle", false, false);
                // }

                this.rootElement.appendChild(this._gridContainer);

                this._deviceEntityGrid = new EntityGrid({
                    gridKey: this.model._gridKey,
                    userGridData: this._model._userGridData,
                    logger: this._logger,
                    singleSelect: false,
                    columns: this._model.columnConfig,
                    pagingFeed: this._model.pagingFeed,
                    selectedRows: _.get(this._model.getSelectedDeviceIds(), "selectedRows", []),
                    showHeader: true,
                    showLinesNumber: this._model._gameEditor.renderer._rootElem.querySelector(
                        "game-editor-view-actions-gl left-container"
                    ),
                    columnSettingsWidgetContainer:
                        this._model._gameEditor.renderer._rootElem.querySelector(".ge-header-left"),
                    groupingContainer:
                        this._model._gameEditor.renderer._rootElem.querySelector(".ge-header-left"),
                    filterComponent:
                        this._model._gameEditor.renderer._rootElem.querySelector(".ge-header-left"),
                    advancedFilterComponent:
                        this._model._gameEditor.renderer._rootElem.querySelector(".ge-header-left"),
                    modalUserFeedback: this._model._modalUserFeedback,
                    groupingValuesFeed: new DistinctValuesFeed({
                        startFeed: (fieldName, query, replacementValue) => {
                            if (replacementValue) {
                                return this.model._DeviceEntityDef.startDeviceDistinctValuesFeed(
                                    this.model.activeOrgId,
                                    fieldName,
                                    query,
                                    replacementValue
                                );
                            }
                            return this.model._DeviceEntityDef.startDeviceDistinctValuesFeed(
                                this.model.activeOrgId,
                                fieldName,
                                query
                            );
                        },
                        replacementValue: "Blank",
                    }),
                    additionalFilter,
                    externalFilter: this._model._filterForTree,
                });

                if (!this._gridContainer.hasChildNodes()) {
                    this._gridContainer.appendChild(this._deviceEntityGrid.renderer.rootElement);
                }

                this._renderAvailableActions();
                this._onFeedRefreshView();
                this._registerListeners();
            })
            .then(() => {
                const openedTabs = this.model.explorerState.openedTabs;
                let openedTabsIds = Object.keys(openedTabs);

                if (this._model._gameEditor._filterFromUrl) {
                    this._deviceEntityGrid._advancedFilter._advancedFilter._renderer._applyQuery();
                }

                if (
                    this._model._gameEditor._openEditorFromUrl &&
                    this._model._gameEditor._openEditorFromUrl.open &&
                    !_.isUndefined(this._model._gameEditor._openEditorFromUrl.selectedIds)
                ) {
                    openedTabsIds = this._model._gameEditor._openEditorFromUrl.selectedIds;
                }

                const activeTabId = this.model.explorerState.activeTab;
                let activeTab = openedTabs[activeTabId];

                if (
                    openedTabs &&
                    openedTabsIds.length > 0 &&
                    this.model.gameEditor._editorLayout.launchedEditors.length === 0
                ) {
                    delete openedTabsIds[activeTabId];

                    openedTabsIds.forEach((tabId) => {
                        if (!this._model._gameEditor._openEditorFromUrl.open) {
                            const openedTab = openedTabs[tabId],
                                ids = openedTab.deviceIds;
                            if (typeof openedTab === "object" && tabId !== activeTabId) {
                                this.model.openDeviceInstanceEditor(ids, openedTab.fqn);
                            }
                        } else {
                            this.model.openDeviceInstanceEditor(tabId);
                            activeTab = undefined;
                        }
                    });

                    // Open the active tab at the end
                    if (typeof activeTab === "object") {
                        this.model.openDeviceInstanceEditor(activeTab.deviceIds, activeTab.fqn);
                    }
                }

                if (
                    this._model._gameEditor._openNetworkDiscoveryFromUrl &&
                    this._model._gameEditor._openNetworkDiscoveryFromUrl.open
                ) {
                    const openDiscoveryWithDevices = !_.isUndefined(
                        this._model._gameEditor._openNetworkDiscoveryFromUrl.selectedIds
                    )
                        ? this._model._gameEditor._openNetworkDiscoveryFromUrl.selectedIds
                        : [];
                    this._model.openNetworkDiscoveryEditor(openDiscoveryWithDevices, true);
                }

                //image server status : ONLY ON OSM
                if (this._model._solutionName === "os_manager") {
                    this._model._statusBar.render();
                    this.rootElement.parentElement.parentElement
                        .querySelector("game-editor-view-actions-gl left-container")
                        .appendChild(this._model._statusBar.rootElement);
                }
                // if (!_.isEmpty(this._filterForTree)) {
                //     setTimeout(() => {
                //         this._deviceEntityGrid.setExternalFilter(this._filterForTree);
                //     }, 400);
                // }
            })
            .catch((error) => {
                this._logger.warn(
                    {error},
                    "DeviceGridViewRenderer.render  --  model is not ready -- error"
                );
            });
    }

    get rootElement() {
        return this._rootElem;
    }

    get model() {
        return this._model;
    }

    get selectedRows() {
        return this._deviceEntityGrid.getSelectedRows();
    }

    restartGrid() {
        this._unregisterGridListeners();
        return this._deviceEntityGrid.shutdown().then(() => {
            this._instantiateGrid();
            this._gridContainer.appendChild(this._deviceEntityGrid.renderer.rootElement);
            this._registerGridListeners();
            this._renderAvailableActions();
        });
    }

    /*******************************************/
    /* END COMPONENT RENDERER METHODS          */
    /* START PRIVATE RENDER METHODS            */
    /*******************************************/

    _showDefaultMessageDisplay() {
        let message;
        switch (this._model._solutionName) {
            case "device_manager":
                message = {
                    message: "Add a device",
                    icon: "configdrift.add_device",
                    action: Registrations.DEVICE_EXPLORER_ACTION_CREATE,
                };
                break;
            case "config_drift":
            case "os_manager":
            case "data_explorer":
                message = {
                    message: "Add devices in Device Manager",
                };
                break;
        }
        return this._model.gameEditor
            .showDefaultMessageView(message, this._gridContainer)
            .then(() => {
                this._defaultViewActive = true;
            })
            .catch((err) => {
                this._logger.warn(
                    {error: err},
                    "DeviceListViewerRenderer._showDefaultMessageDisplay error"
                );
            });
    }

    _hideDefaultMessageDisplay() {
        return this._model.gameEditor
            .hideDefaultMessageView()
            .then(() => {
                this._defaultViewActive = false;
            })
            .catch((err) => {
                this._logger.warn(
                    {error: err},
                    "DeviceListViewerRenderer._hideDefaultMessageDisplay error"
                );
            });
    }

    _expandTree() {
        // Calling querySelector to then pass the DOM element to $ is a workaround as jQuery sometimes doesn't find tree-container on first load
        // console.log(this);
        const treeContainerElement =
            this._model._gameEditor.renderer._rootElem.querySelector("tree-container");
        const tv = $(treeContainerElement).data("kendoTreeView");
        const userdata = this._userDataStorage.getData("site_tree_collapsed_data", true);
        let itemsToExpand;
        if (!userdata || _.isEmpty(userdata) || !userdata[this.model.activeOrgId]) {
            itemsToExpand = undefined;
        } else {
            itemsToExpand = Object.keys(userdata[this.model.activeOrgId]);
        }
        // console.log('tv', tv);
        TreeUtils.expandItemsFromCollapsed(itemsToExpand, tv.dataItems(), tv);
    }

    _moCallback(mutationList /*, observer*/) {
        mutationList.forEach((mutation) => {
            if (mutation.type === "attributes" && mutation.attributeName === "aria-busy") {
                this._expandTree();
            }
        });
    }

    _setExpandedSitesData(tv) {
        if (!tv) {
            return;
        }
        const collapsedSites = TreeUtils.getCollapsedSites(tv.dataItems());
        const userdata = this._userDataStorage.getData("site_tree_collapsed_data", true);
        let collapsedSiteData;
        if (!userdata || _.isEmpty(userdata)) {
            collapsedSiteData = {};
        } else {
            collapsedSiteData = userdata;
        }
        collapsedSiteData[this.model.activeOrgId] = collapsedSites;
        this._userDataStorage.setData(collapsedSiteData, "site_tree_collapsed_data");
    }

    _renderSiteTree() {
        const geHeaderL =
            this._model._gameEditor.renderer._rootElem.querySelector(".ge-header-left");
        const existingTreeToggle = geHeaderL.querySelector(".treeview-toggle");
        if (existingTreeToggle) {
            existingTreeToggle.remove();
        }
        geHeaderL.appendChild(this._panelEl);
        this.rootElement.appendChild(this._treeContainer);
        this.rootElement.appendChild(this._resizer);

        let treeDat = this._model.treeData;
        this._treeRootName = _.get(this._model, "_userSession.activeOrganization.name", "Root");

        const kendoTemplate = kendo.template(
            `#if(item._pristineProperties.isRoot) {#<span class='g-icon-globe'>#} else {#<span class='g-icon-#=item.icon#' data-path='#=item._pristineProperties.path#'>#}#
                </span> #= item._pristineProperties.name #
                    `
        );

        // Calling querySelector to then pass the DOM element to $ is a workaround as jQuery sometimes doesn't find tree-container on first load
        const treeContainerElement =
            this._model._gameEditor.renderer._rootElem.querySelector("tree-container");
        const targetNode = treeContainerElement;
        const observerOptions = {
            attributes: true,
            subtree: false,
        };

        this._moObserver = new MutationObserver(this._moCallback);
        this._moObserver.observe(targetNode, observerOptions);
        this._treeComponent = $(treeContainerElement).kendoTreeView({
            template: kendoTemplate,
            dataSource: treeDat,
            dragAndDrop: false,
            checkboxes: false,
            loadOnDemand: false,
            dataTextField: "_pristineProperties.name",
            dataSpriteCssClassField: "sprite",
            select: (e) => {
                let tv = $("tree-container").data("kendoTreeView");
                let node = tv.dataItem(e.node);
                this._model._selectedNode = node;
                this._updateFilter(node);
            },
            expand: () => {
                const tv = $("tree-container").data("kendoTreeView");
                // console.log("expand");
                // console.log(e);
                this._setExpandedSitesData(tv);
            },
            collapse: () => {
                const tv = $("tree-container").data("kendoTreeView");
                // console.log("collapse");
                // console.log(e);
                this._setExpandedSitesData(tv);
            },
        });
    }

    // _onFilterChange() {
    // if (this._model._selectedNode && !_.isEmpty(this._model._filterForTree)) {
    //     let pagingFilter = this._model.pagingFeed.getFilterData();
    //     if (pagingFilter.$userQuery) {
    //         pagingFilter = pagingFilter.$userQuery;
    //     }

    //     const filter = Array.isArray(pagingFilter) ?
    //         { "$and": pagingFilter.concat([this._model._filterForTree]) } :
    //         {
    //             "$and": [
    //                 pagingFilter,
    //                 this._model._filterForTree
    //             ]
    //         };
    //     this._model.pagingFeed.setFilter(filter);
    // }
    // }

    _updateFilter(node, panelHidden = false, silent = false) {
        if (node === "ontoggle") {
            if (panelHidden) {
                this._deviceEntityGrid.clearExternalFilter(silent);
            } else {
                if (!_.isEmpty(this._model._filterForTree)) {
                    this._deviceEntityGrid.setExternalFilter(this._model._filterForTree, silent);
                } else {
                    this._deviceEntityGrid.clearExternalFilter(silent);
                }
            }
        } else {
            const path = node?._pristineProperties?.path;
            if (path) {
                if (path === "/") {
                    this._model._filterForTree = {};
                } else {
                    const pathVals = TreeUtils.getAllChildrenPaths(node);
                    if (pathVals.length < 50) {
                        this._model._filterForTree = {sitePath: {$in: pathVals}};
                    } else {
                        this._model._filterForTree = {sitePath: {$regex: "^" + path + "(/|$)"}};
                    }
                }
                this._userDataStorage.setData(node._id, "site_tree_selected");
                this._userDataStorage.setData(
                    JSON.stringify(this._model._filterForTree),
                    "site_tree_filter_data"
                );
                this._deviceEntityGrid.setExternalFilter(this._model._filterForTree);
            } else {
                this._deviceEntityGrid.clearExternalFilter(silent);
                this._model._filterForTree = {};
                this._userDataStorage.setData("", "site_tree_selected");
            }
        }
    }

    /*******************************************/
    /* END PRIVATE RENDER METHODS              */
    /* START HANDLER METHODS                   */
    /*******************************************/

    _onGridBusyStart() {
        if (this._model.pagingFeed.totalEntities > this._model.pagingFeed.pageSize * 2) {
            let gridBusyEnded = false;
            this._deviceEntityGrid.once("GridBusyEnd", () => {
                gridBusyEnded = true;
            });
            this._model._modalUserFeedback.displayLoading().then(() => {
                if (gridBusyEnded) {
                    this._model._modalUserFeedback.hideDisplayed();
                } else {
                    this._deviceEntityGrid.once("GridBusyEnd", () => {
                        this._model._modalUserFeedback.hideDisplayed();
                    });
                }
            });
        }
    }

    _onFeedInitialized() {
        // this._renderGridRows();
        this._renderAvailableActions();
    }

    _onUserSorted(eventData) {
        this._model.onUserSorted(eventData);
    }

    _onUserDblClickedRow(obj) {
        if (!obj.selectedRow) {
            return;
        }
        const controlType = _.get(
            this._model,
            ["pagingFeed", "cache", obj.selectedRow, "baseControlType"],
            "NA"
        );
        let view = Registrations.DIE_VIEW_DETAILS;
        if (this._model._solutionName === "config_drift") {
            view =
                this._userDataStorage.getData("CD_active_view", true) ||
                Registrations.DIE_VIEW_DETAILS;
            if (
                controlType === "CONTROLLER" &&
                (view === Registrations.DIE_VIEW_CONFIG || view === Registrations.DIE_VIEW_COMPARE)
            ) {
                view = Registrations.DIE_VIEW_DETAILS;
            }
            this._model.openDeviceInstanceEditor(obj.selectedRow, view);
        } else if (this._model._solutionName === "os_manager") {
            view =
                this._userDataStorage.getData("OSM_active_view", true) ||
                Registrations.DIE_VIEW_DETAILS;
            this._model.openDeviceInstanceEditor(obj.selectedRow, view);
        } else if (this._model._solutionName === "data_explorer") {
            view =
                this._userDataStorage.getData("DE_active_view", true) ||
                Registrations.DIE_VIEW_DETAILS;
            this._model.openDeviceInstanceEditor(obj.selectedRow, view);
        } else {
            view =
                this._userDataStorage.getData("DM_active_view", true) ||
                Registrations.DIE_VIEW_DETAILS;
            if (
                controlType === "CONTROLLER" &&
                (view === Registrations.DIE_VIEW_CONFIG || view === Registrations.DIE_VIEW_COMPARE)
            ) {
                view = Registrations.DIE_VIEW_DETAILS;
            }
            this._model.openDeviceInstanceEditor(obj.selectedRow, view);
        }
    }

    _onGridUserSelectedRows(selectedRowIds) {
        this._model._gameEditor._editorLayout.emit("UserSelectedRows", selectedRowIds || []);
        this._model.updateExplorerState("selectedIds", selectedRowIds || []);
        this._renderAvailableActions();
    }

    _onFeedRefreshView() {
        const totalEntities = this._model.pagingFeed.totalEntities;
        const filterData = this._model.pagingFeed.getFilterData();
        if (totalEntities === 0 && _.isEmpty(_.get(filterData, "$userQuery", filterData))) {
            if (this._defaultViewActive === false) {
                this._showDefaultMessageDisplay();
            }
        } else {
            if (this._defaultViewActive === true) {
                this._hideDefaultMessageDisplay();
            }
        }
    }
    _onGridColumnUpdated() {
        this._model.gameEditor.onGridColumnUpdated();
    }

    _onSelectRowsOnDeviceGrid(rowIdsToSelect) {
        this._deviceEntityGrid.setSelectedRows(rowIdsToSelect);
    }

    _onOrgChanged() {
        this._userDataStorage.setData("", "site_tree_selected");
        this._model._filterForTree = {};
        this._userDataStorage.setData(
            JSON.stringify(this._model._filterForTree),
            "site_tree_filter_data"
        );
    }

    _onResizerMousedown(e) {
        let treeWidth = this._treeContainer.style.width;
        e = e || window.event;
        let start = 0,
            diff = 0;
        if (e.pageX) {
            start = e.pageX;
        } else if (e.clientX) {
            start = e.clientX;
        }

        document.body.onmousemove = (e) => {
            e = e || window.event;
            let end = 0;
            if (e.pageX) {
                end = e.pageX;
            } else if (e.clientX) {
                end = e.clientX;
            }

            diff = end - start;
            let updatedTreeWidth = this._treeContainer.style.width;
            if (parseInt(updatedTreeWidth, 10) > 100 || diff > 0) {
                this._treeContainer.style.width = parseInt(treeWidth, 10) + diff + "px";
            }
        };
        document.body.onmouseup = () => {
            this._userDataStorage.setData(
                parseInt(this._treeContainer.style.width, 10),
                "treeContainerWidth"
            );
            document.body.onmousemove = document.body.onmouseup = null;
        };
    }

    /**
     * Toggles tree panel visibility with deferred regeneration optimization.
     * When hidden, expensive tree rebuilds are deferred until tree is shown.
     */
    _toggleTree() {
        this._treeContainer.classList.toggle("hidden");
        this._resizer.classList.toggle("hidden");
        this._panelEl.classList.toggle("active");

        const isSitePanelHidden = this._treeContainer.classList.contains("hidden");
        this._model.gameEditor._onSitePanelVisibilityChanged(!isSitePanelHidden);

        if (isSitePanelHidden) {
            this._updateFilter("ontoggle", true);
            this._isTreeVisible = false;
        } else {
            this._updateFilter("ontoggle", false);
            this._isTreeVisible = true;

            if (this._hasPendingTreeRegeneration) {
                this._onRegenerateTreeData();
            }
        }

        let toggleData = this._userDataStorage.getData("site_tree_toggle_data", true) || {};
        toggleData[this.model.activeOrgId] = !isSitePanelHidden;
        this._userDataStorage.setData(toggleData, "site_tree_toggle_data");
    }

    /**
     * Regenerates tree data from backend (expensive operation).
     * Deferred when tree is hidden to avoid unnecessary work.
     */
    _onRegenerateTreeData() {
        if (!this._isTreeVisible) {
            this._hasPendingTreeRegeneration = true;
            return;
        }

        this._hasPendingTreeRegeneration = false;

        let tv = $("tree-container").data("kendoTreeView");
        this._model.treeData = TreeUtils.generateTreeNodesFromRaw(
            this._model.gameEditor.sitePaths,
            "path"
        ).items;

        let treeDat = this._model.treeData;
        tv.setDataSource(treeDat);
        const selectedPath = _.get(
            this._model,
            "_selectedNode._pristineProperties.path",
            _.get(this._model, "_selectedNode._pristineProperties.name")
        );
        if (selectedPath) {
            setTimeout(() => {
                const elWithPath = this._rootElem.querySelector(
                    'span[data-path="' + selectedPath + '"'
                );
                if (elWithPath) {
                    tv = $("tree-container").data("kendoTreeView");
                    const nodeEl = $(elWithPath).closest(".k-item");
                    const upNode = tv.dataItem(nodeEl);
                    tv.select(nodeEl);
                    this._model._selectedNode = upNode;
                    this._updateFilter(upNode);
                    return;
                }
                this._updateFilter();
                return;
            }, 500);
        } else {
            this._updateFilter();
        }
    }

    /*******************************************/
    /* END HANDLER METHODS                     */
    /* START PRIVATE API METHODS               */
    /*******************************************/

    // TODO - Nobody should be using methods on a Renderer directly so make all these methods private

    /*******************************************/
    /*  END PRIVATE API METHODS */
    /*******************************************/
}

module.exports = DeviceGridViewRenderer;
